import { Colors, Icon, Pills } from '@amelio/ui-library';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type ProfileCardProps = {
    profile: CompetencyProfile;
    counts: {
        activeCount: number;
        priorityCount: number;
    };
    /** Whom the profile's competencies are adjusted for; absent, the profile is taken as it is. */
    adjustedFor?: string;
    onSelectAnother: () => void;
};

export const ProfileCard = ({
    profile,
    counts,
    adjustedFor,
    onSelectAnother,
}: ProfileCardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    return (
        <div className={'w-full max-w-[448px]'}>
            <div
                className={
                    'border-primary flex min-h-[111px] flex-col justify-between rounded-[10px] border bg-white p-[24px] shadow-[0_4px_10px_rgba(40,48,77,0.12)]'
                }
            >
                <div className={'ds-mb-2xl flex items-start justify-between gap-3'}>
                    <p className={'text-navy text-[16px] leading-tight font-semibold'}>
                        {getTextByLanguage(profile.name, language)}
                    </p>

                    <span
                        className={
                            'bg-primary flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'essential-checkmark'}
                            size={'md'}
                            color={Colors.WHITE}
                        />
                    </span>
                </div>

                <div className={'mt-3 flex flex-col items-center gap-2 sm:flex-row'}>
                    <span
                        className={
                            'text-caption text-purple rounded-full bg-purple-100 px-2 py-0.5'
                        }
                    >
                        {t('competencies.competencyProfilesAdmin.selector.skillCount', {
                            count: counts.activeCount,
                        })}
                    </span>

                    <span
                        className={
                            'text-caption bg-primary-100 rounded-full px-2 py-0.5 text-teal-500'
                        }
                    >
                        {t('competencies.competencyProfilesAdmin.selector.priorityCount', {
                            count: counts.priorityCount,
                        })}
                    </span>

                    {adjustedFor && (
                        <Pills
                            variant={'default'}
                            size={'sm'}
                            className={'whitespace-nowrap'}
                        >
                            {t('competencies.competencyProfilesAdmin.selector.adjustedFor', {
                                name: adjustedFor,
                            })}
                        </Pills>
                    )}
                </div>
            </div>

            <button
                type={'button'}
                onClick={onSelectAnother}
                className={
                    'text-navy/60 hover:text-primary text-body-sm mt-2 ml-auto flex items-center gap-1 transition-colors'
                }
                data-testid={'roster-selector-reset'}
            >
                <Icon
                    name={'arrow-refresh'}
                    size={'xs'}
                />
                {t('competencies.competencyProfilesAdmin.selector.selectAnother')}
            </button>
        </div>
    );
};
